import { collection, onSnapshot, orderBy, query, type Timestamp } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';

/**
 * お知らせ機能 (docs/04 §4-3, REQ-4.1.25 / REQ-4.3.33)。
 * Phase 5 user 第 X 弾 (admin 第 15 弾の後追い):
 *   - pattern: 'banner' | 'modal' 両方を hook で公開
 *   - forceReshow=true は seenIds を無視して再表示
 *   - targetSessionIds は将来の session context 導入時に filter 追加予定 (現状は無視 = 全員表示)
 *
 * 旧 `apps/user/src/lib/announcements.ts` + `AnnouncementHost.tsx` (L3 参照、docs/97 §3)。
 * 旧との差分:
 * - body は `message` 後方互換廃止 (新環境のため不要)
 * - seen は localStorage 直、Phase 4 で IndexedDB に置換予定
 * - forceReshow: 旧未実装、admin 第 15 弾で追加された field を踏襲
 */
export type AnnouncementLevel = 'info' | 'warning' | 'critical';
export type AnnouncementPattern = 'banner' | 'modal';

// shared-types の `Announcement` は wire format (startAt/endAt が ISO string、optional field 多数)、
// この client repr は Timestamp → Date 変換後・null 統一の形。意図的に分離 (admin 側も同様の repr 分離)。
export interface Announcement {
  id: string;
  title: string;
  body: string;
  level: AnnouncementLevel;
  pattern: AnnouncementPattern;
  startAt: Date | null;
  endAt: Date | null;
  archivedAt: Date | null;
  forceReshow: boolean;
  targetSessionIds: string[] | null;
}

const SEEN_KEY = 'cw.announcement.seenIds.v1';

function loadSeen(): Set<string> {
  if (typeof localStorage === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function persistSeen(seen: Set<string>): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...seen]));
  } catch {
    /* quota / private mode 等は無視 */
  }
}

/** お知らせが現在有効か判定 (旧 isAnnouncementActive 踏襲、docs/97 §5-1 状態遷移) */
export function isAnnouncementActive(a: Announcement, now: Date = new Date()): boolean {
  if (a.archivedAt) return false;
  if (a.endAt && a.endAt < now) return false;
  if (a.startAt && a.startAt > now) return false;
  return true;
}

/**
 * user に対して表示すべきかを判定。
 * - active でないものは表示しない
 * - forceReshow=true は seenIds を無視 (admin 意図的に再表示)
 * - それ以外で seenIds に含まれているものは表示しない
 * - targetSessionIds が非空かつ現セッションが含まれない場合は表示しない (session 導入後)
 */
export function isAnnouncementVisible(
  a: Announcement,
  seenIds: Set<string>,
  now: Date = new Date(),
  currentSessionId: string | null = null,
): boolean {
  if (!isAnnouncementActive(a, now)) return false;
  if (a.targetSessionIds && a.targetSessionIds.length > 0) {
    if (!currentSessionId || !a.targetSessionIds.includes(currentSessionId)) return false;
  }
  if (a.forceReshow) return true;
  return !seenIds.has(a.id);
}

export type AnnouncementsState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: Announcement[]; error: null }
  | { status: 'error'; items: []; error: Error };

export function useAnnouncements(districtId: string | null): {
  state: AnnouncementsState;
  seenIds: Set<string>;
  markSeen: (id: string) => void;
} {
  const [state, setState] = useState<AnnouncementsState>({
    status: 'loading',
    items: [],
    error: null,
  });
  const [seenIds, setSeenIds] = useState<Set<string>>(() => loadSeen());

  useEffect(() => {
    // 地区フォールバック根絶: districtId 未解決 (null/空) のときは phase2-default の
    // announcements を購読しない (お知らせ未配信バグの根を断つ)。
    if (!districtId) return;
    const col = collection(db, 'districts', districtId, 'announcements');
    const q = query(col, orderBy('startAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items: Announcement[] = snap.docs.map((d) => {
          const data = d.data();
          const toDate = (v: unknown): Date | null =>
            v && typeof (v as Timestamp).toDate === 'function' ? (v as Timestamp).toDate() : null;
          const targetSessionIds = Array.isArray(data.targetSessionIds)
            ? (data.targetSessionIds as string[])
            : null;
          return {
            id: d.id,
            title: (data.title as string) ?? '',
            body: (data.body as string) ?? '',
            level: (data.level as AnnouncementLevel | undefined) ?? 'info',
            pattern: (data.pattern as AnnouncementPattern | undefined) ?? 'banner',
            startAt: toDate(data.startAt),
            endAt: toDate(data.endAt),
            archivedAt: toDate(data.archivedAt),
            forceReshow: data.forceReshow === true,
            targetSessionIds,
          };
        });
        setState({ status: 'ready', items, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return unsubscribe;
  }, [districtId]);

  function markSeen(id: string): void {
    setSeenIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      persistSeen(next);
      return next;
    });
  }

  return { state, seenIds, markSeen };
}
